// Utility functions for date formatting on cards
// 🚨 CRITICAL FIX (Jan 4, 2026): Prevent timezone conversion causing off-by-one day errors
// Database stores: "2026-01-05" (Monday, January 5)
// Without fix: Shows "Jan 4" (WRONG - off by 1 day)
// With fix: Shows "Jan 5" (CORRECT - matches database)

/**
 * Format a date string for display, returning '—' for empty/invalid values.
 * Uses UTC noon to avoid timezone shifts (same as formatCardDateShort).
 */
export function safeFormatCardDate(
  value: string | null | undefined,
  options: { includeYear?: boolean } = {}
): string {
  const raw = value ?? '';
  if (raw === '') return '—';
  const dateStr = raw.match(/^\d{4}-\d{2}-\d{2}/) ? raw.substring(0, 10) : '';
  if (dateStr === '') return '—';
  const parsed = new Date(dateStr + 'T12:00:00Z');
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(options.includeYear ? { year: 'numeric' } : {}),
    timeZone: 'UTC',
  });
}

export const formatCardDate = (date: Date): string => {
  // Convert to UTC date at noon to avoid timezone shifts
  const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  const dateAtNoon = new Date(dateStr + 'T12:00:00Z');
  
  return dateAtNoon.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC' // Force UTC to match database dates exactly
  });
};

/** Long format for headers: "Thursday, February 12, 2026". Uses UTC noon to avoid timezone shifts. */
export const formatCardDateLong = (date: Date | string): string => {
  let dateStr: string;
  if (typeof date === 'string') {
    dateStr = date.match(/^\d{4}-\d{2}-\d{2}/) ? date.substring(0, 10) : new Date(date).toISOString().split('T')[0];
  } else {
    // Use UTC methods to avoid timezone shift when Date was created from a date-only string
    // (e.g. new Date("2026-03-16") = UTC midnight, which is previous day in western timezones)
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  }
  const dateAtNoon = new Date(dateStr + 'T12:00:00Z');
  return dateAtNoon.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

export const formatCardDateShort = (date: Date | string): string => {
  // 🔧 FIX JAN 23, 2026: Handle timezone-agnostic date formatting
  // Database stores dates as DATE type (YYYY-MM-DD, no timezone)
  // We need to format this date without timezone conversion
  
  let dateStr: string;
  
  if (typeof date === 'string') {
    // If already a string, extract YYYY-MM-DD
    if (date.match(/^\d{4}-\d{2}-\d{2}/)) {
      dateStr = date.substring(0, 10);
    } else {
      // Fallback for other formats
      dateStr = new Date(date).toISOString().split('T')[0];
    }
  } else {
    // If Date object, extract LOCAL date components (avoid UTC conversion)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    dateStr = `${year}-${month}-${day}`;
  }
  
  // Parse as UTC at noon to prevent timezone shifts during formatting
  const dateAtNoon = new Date(dateStr + 'T12:00:00Z');
  // Month + number only (e.g. "Feb 13"), no day of week
  return dateAtNoon.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  });
};

export const formatRelativeDate = (date: Date): string => {
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays} days ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)} months ago`;
  return `${Math.floor(diffInDays / 365)} years ago`;
};

export const getCardTimestamp = (
  item: { 
    createdAt: Date; 
    scheduledDate?: Date | string; 
    completedAt?: Date; 
    updatedAt: Date;
    status?: string;
  }
): { date: Date; label: string } => {
  // Prioritize completion date for completed items
  if (item.completedAt && (item.status === 'completed')) {
    return { date: item.completedAt, label: 'Completed' };
  }
  
  // Use scheduled date for active/upcoming items
  if (item.scheduledDate && (item.status === 'active' || !item.status)) {
    const d = typeof item.scheduledDate === 'string' ? new Date(item.scheduledDate) : item.scheduledDate;
    return { date: d, label: 'Scheduled' };
  }
  
  // Fall back to creation date
  return { date: item.createdAt, label: 'Created' };
};

export const getCardTimestampForPlan = (
  plan: { 
    createdAt: Date; 
    weekStartDate: Date; 
    weekEndDate: Date; 
    updatedAt: Date;
    state?: string;
  }
): { date: Date; label: string } => {
  // For plans, use week start date as the primary timestamp
  if (plan.state === 'completed') {
    return { date: plan.weekEndDate, label: 'Week of' };
  }
  
  return { date: plan.weekStartDate, label: 'Week of' };
};

// Sorting function for chronological order (most recent first)
export const sortByTimestamp = <T extends { createdAt: Date; scheduledDate?: Date; completedAt?: Date; updatedAt: Date }>(
  items: T[], 
  direction: 'desc' | 'asc' = 'desc'
): T[] => {
  return [...items].sort((a, b) => {
    const aTimestamp = getCardTimestamp(a).date;
    const bTimestamp = getCardTimestamp(b).date;
    
    return direction === 'desc' 
      ? bTimestamp.getTime() - aTimestamp.getTime()
      : aTimestamp.getTime() - bTimestamp.getTime();
  });
}; 